import React, { useEffect, useState } from "react";
import { Button, Card, Col, Nav, ProgressBar, Row, Table, Alert } from "react-bootstrap";
import { Link } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import ReactApexChart from "react-apexcharts";
import { Doughnut } from 'react-chartjs-2';
import createUserInitService from '../services/userInitService';
import { useAuth } from '../utils/AuthContext';
import { useBudget } from '../hooks/useBudget';
import { useExpense } from '../hooks/useExpense';
import { useIncome } from '../hooks/useIncome';
import { useBill } from '../hooks/useBill';
import { useGoal } from '../hooks/useGoal';
import moment from 'moment';
import { dp3 } from "../data/DashboardData";

export default function FinancialAnalytics() {
    const { currentUser } = useAuth();
    const { stats: budgetStats, loading: budgetLoading, budgets } = useBudget();
    const { stats: expenseStats, loading: expenseLoading } = useExpense();
    const { stats: incomeStats, loading: incomeLoading } = useIncome();
    const { stats: billStats, loading: billLoading } = useBill();
    const { goals, loading: goalLoading } = useGoal();

    const [timeFrame, setTimeFrame] = useState('year');
    const [dateRange, setDateRange] = useState({
        start: moment().startOf('year'),
        end: moment().endOf('year')
    });

    const loading = budgetLoading || expenseLoading || incomeLoading || billLoading || goalLoading;

    const userInitService = createUserInitService(currentUser.uid);

    const seriesOne = [{
        name: 'series1',
        data: dp3
    }, {
        name: 'series2',
        data: dp3
    }];

    const optionOne = {
        chart: {
            parentHeightOffset: 0,
            type: 'area',
            toolbar: { show: false },
            stacked: true
        },
        colors: ['#4f6fd9', '#506fd9'],
        grid: {
            borderColor: 'rgba(72,94,144, 0.08)',
            padding: { top: -20 },
            yaxis: {
                lines: { show: false }
            }
        },
        stroke: {
            curve: 'straight',
            width: [2, 0]
        },
        xaxis: {
            type: 'numeric',
            tickAmount: 13,
            axisBorder: { show: false },
            labels: {
                style: {
                    colors: '#6e7985',
                    fontSize: '11px'
                }
            },
        },
        yaxis: {
            min: 0,
            max: 100,
            show: false
        },
        fill: {
            type: 'gradient',
            gradient: {
                opacityFrom: 0.5,
                opacityTo: 0,
            }
        },
        dataLabels: { enabled: false },
        legend: { show: false },
        tooltip: { enabled: false }
    };

    const getTopExpenseCategories = () => {
        try {
            // Get monthly stats from expenseStats
            const categoryStats = expenseStats.categoryStats || {};


            // Sort categories by amount and get top 3
            const sortedCategories = Object.entries(categoryStats)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 3);

            // Calculate total expenses
            const totalExpense = sortedCategories.reduce((sum, [, amount]) => sum + amount.total, 0);


            // If no categories found, return empty data
            if (sortedCategories.length === 0) {
                return {
                    data: [],
                    colors: ['#506fd9', '#85b6ff', '#33d685']
                };
            }

            return {
                data: sortedCategories.map((category) => ({
                    category: category[0],
                    amount: category[1].total,
                    percentage: totalExpense > 0 ? ((category[1].total / totalExpense) * 100).toFixed(1) : '0.0'
                })),
                colors: ['#506fd9', '#85b6ff', '#33d685']
            };
        } catch (error) {
            console.error('Error calculating top expense categories:', error);
            return {
                data: [],
                colors: ['#506fd9', '#85b6ff', '#33d685']
            };
        }
    };

    const topCategories = getTopExpenseCategories();
    const dataPie = {
        labels: topCategories.data.map(item => item.category),
        datasets: [{
            data: topCategories.data.map(item => item.amount),
            backgroundColor: topCategories.colors
        }]
    };

    var optionPie = {
        maintainAspectRatio: false,
        responsive: true,
        plugins: {
            legend: {
                display: false
            }
        },
        animation: {
            animateScale: true,
            animateRotate: true
        }
    };

    const getMonthlyData = () => {
        // Get current date and start of year
        const currentDate = moment();
        const startOfYear = moment().startOf('year');
        const months = [];
        const fixedData = [];
        const variableData = [];

        // Generate array of last 12 months
        for (let i = 0; i < 12; i++) {
            const monthDate = moment(startOfYear).add(i, 'months');
            const monthKey = monthDate.format('YYYY-MM');
            const monthStats = expenseStats.monthlyStats?.[monthKey] || {};

            months.push(monthDate.format('MMM YY'));
            fixedData.push(monthStats.fixed || 0);
            variableData.push(monthStats.variable || 0);
        }

        return { months, fixedData, variableData };
    };

    const { months, fixedData, variableData } = getMonthlyData();

    const seriesEight = [{
        name: 'Fixed Expenses',
        type: 'column',
        data: fixedData
    }, {
        name: 'Variable Expenses',
        type: 'column',
        data: variableData
    }];

    const optionEight = {
        chart: {
            parentHeightOffset: 0,
            stacked: true,
            toolbar: { show: false }
        },
        grid: {
            borderColor: 'rgba(72,94,144, 0.07)',
            padding: {
                top: -20,
                left: 5,
                bottom: -15
            }
        },
        states: {
            hover: {
                filter: { type: 'none' }
            },
            active: {
                filter: { type: 'none' }
            }
        },
        colors: ['#506fd9', '#85b6ff'],
        plotOptions: {
            bar: {
                columnWidth: '40%',
                borderRadius: 3,
                dataLabels: {
                    total: {
                        enabled: true,
                        style: {
                            fontSize: '13px'
                        },
                        formatter: function (val) {
                            if (typeof val !== 'number' || isNaN(val) || val <= 0) return '';
                            return '$' + val.toFixed(0);
                        }
                    }
                }
            }
        },
        stroke: {
            curve: 'straight',
            lineCap: 'square',
            width: 0
        },
        xaxis: {
            categories: months,
            labels: {
                style: {
                    colors: '#6e7985',
                    fontSize: '10px',
                    fontWeight: '500'
                }
            }
        },
        tooltip: { enabled: false }
    };

    const series6 = [{
        name: 'Income',
        type: 'column',
        data: Object.values(incomeStats.monthlyStats || {}).map(month => month.total || 0)
    }, {
        name: 'Expenses',
        type: 'column',
        data: Object.values(expenseStats.monthlyStats || {}).map(month => month.total || 0)
    }];

    const option6 = {
        chart: {
            height: 180,
            parentHeightOffset: 0,
            stacked: false,
            toolbar: {
                show: false
            }
        },
        colors: ['#506fd9', '#85b6ff'],
        grid: {
            borderColor: 'rgba(72,94,144, 0.07)',
            padding: {
                top: -20
            },
            yaxis: {
                lines: {
                    show: false
                }
            }
        },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '20%',
                endingShape: 'rounded'
            },
        },
        dataLabels: {
            enabled: false
        },
        stroke: {
            show: true,
            width: 2,
            colors: ['transparent']
        },
        xaxis: {
            categories: Object.keys(incomeStats.monthlyStats || {}).map(month =>
                moment(month).format('MMM')
            ),
        },
        yaxis: {
            labels: {
                style: {
                    colors: '#6e7985',
                    fontSize: '10px'
                }
            }
        },
        legend: {
            show: true,
            position: 'bottom',
            horizontalAlign: 'center',
        },
        tooltip: {
            enabled: false
        }
    };

    const currentSkin = (localStorage.getItem('skin-mode')) ? 'dark' : '';
    const [skin, setSkin] = useState(currentSkin);

    const switchSkin = (skin) => {
        if (skin === 'dark') {
            const btnWhite = document.getElementsByClassName('btn-white');
            for (const btn of btnWhite) {
                btn.classList.add('btn-outline-primary');
                btn.classList.remove('btn-white');
            }
        } else {
            const btnOutlinePrimary = document.getElementsByClassName('btn-outline-primary');
            for (const btn of btnOutlinePrimary) {
                btn.classList.remove('btn-outline-primary');
                btn.classList.add('btn-white');
            }
        }
    };

    useEffect(() => {
        userInitService.initializeUser();
        switchSkin(skin);
    }, [skin]);

    const getBillData = () => {
        const total = billStats.total || 0;
        const paid = billStats.paid || 0;
        const pending = billStats.pending || 0;
        const overdue = billStats.overdue || 0;

        return {
            series: [
                (paid / total) * 100,
                (pending / total) * 100,
                (overdue / total) * 100
            ],
            options: {
                chart: {
                    parentHeightOffset: 0,
                    type: 'donut'
                },
                colors: ['#506fd9', '#85b6ff', '#dc3545'],
                dataLabels: {
                    enabled: false
                },
                grid: {
                    padding: {
                        top: 0,
                        bottom: 0
                    }
                },
                legend: {
                    show: true,
                    position: 'bottom',
                    labels: {
                        colors: '#6e7985'
                    }
                },
                plotOptions: {
                    pie: {
                        donut: {
                            size: '70%',
                            labels: {
                                show: true,
                                total: {
                                    show: true,
                                    label: 'Total Bills',
                                    formatter: function (val) {
                                        return '';
                                    }
                                },
                            }
                        }
                    }
                },
                labels: ['Paid', 'Pending', 'Overdue']
            }
        };
    };

    const billData = getBillData();

    const getMonthlyExpensesBreakdown = () => {
        // Get category stats from expenseStats
        let categoryStats = expenseStats.categoryStats || {};
        const currentMonth = moment().format('YYYY-MM');
        
        
        // Calculate total expenses for current month only
        const total = Object.values(categoryStats).reduce((sum, data) => {
            const monthlyTotal = data.date && moment(data.date).format('YYYY-MM') === currentMonth ? data.total : 0;
            return sum + monthlyTotal;
        }, 0);

        categoryStats = Object.values(categoryStats).map(data => data.date && moment(data.date).format('YYYY-MM') === currentMonth ? data : null).filter(Boolean);

        // Define category colors with distinct colors for each category
        const categoryColors = {
            'Housing': '#506fd9', // Deep Blue
            'Food & Groceries': '#FF6B6B', // Red
            'Transportation': '#FFB900', // Yellow
            'Healthcare': '#45B7D1', // Light Blue
            'Education': '#96CEB4', // Sage Green
            'Clothes': '#FF8F5E', // Orange
            'Pets': '#6C88C4', // Steel Blue
            'Entertainment': '#9B51E0', // Purple
            'Utilities': '#2ECC71', // Green
            'Insurance': '#0DCAF0', // Cyan
            'Shopping': '#FF7EB6' // Pink
        };
        
        // Map categories with their amounts and percentages for current month only
        const categories = Object.entries(categoryStats)
            .map(([name, data]) => ({
                name: data.category,
                amount: data.total || 0,
                percentage: total > 0 ? (((data.total || 0) / total) * 100).toFixed(1) : '0',
                color: categoryColors[name] || '#6e7985'
            }))
            .sort((a, b) => b.total - a.total); // Sort by amount in descending order

        return { categories, total };
    };

    const getMonthlyBudgets = () => {
        const currentMonth = moment().format('YYYY-MM');
        
        const budgetsWithSpent = budgets.map(budget => ({
            ...budget,
            spent: budgetStats.categoryStats[budget.category]?.spent || 0 + budgetStats.billStats[budget.category]?.paid || 0
        }));

        // console.log(budgetsWithSpent);
        const currentBudgets = Object.values(budgetsWithSpent)
            .filter(budget => {
                const startDate = moment(budget.startDate);
                const endDate = moment(budget.endDate);
                const now = moment();
                return now.isBetween(startDate, endDate, 'day', '[]'); // Include start and end dates
            })
            .reduce((acc, budget) => {
                // For each category, keep only the most recent budget
                if (!acc[budget.category] || moment(budget.startDate).isAfter(moment(acc[budget.category].startDate))) {
                    acc[budget.category] = budget;
                }
                return acc;
            }, {});

        // Transform budget data
        const budgetItems = Object.values(currentBudgets)
            .map(budget => {
                const spent = budget.spent || 0;
                const limit = budget.amount || 0;
                const progress = limit > 0 ? Math.min((spent / limit) * 100, 100) : 0;
                
                // Determine color based on spending progress
                let color;
                if (progress >= 90) {
                    color = '#dc3545'; // Red for overspending
                } else if (progress >= 70) {
                    color = '#ffc107'; // Yellow for warning
                } else {
                    color = '#33d685'; // Green for good
                }

                return {
                    name: budget.category,
                    spent,
                    limit,
                    progress,
                    color,
                    startDate: budget.startDate,
                    endDate: budget.endDate
                };
            })
            .sort((a, b) => b.spent - a.spent); // Sort by spent amount

        return { budgetItems };
    };

    const getSavingGoals = () => {
        if (!goals || goals.length === 0) return [];
        
        return goals.map(goal => {
            const progress = goal.currentAmount ? ((goal.currentAmount / goal.targetAmount) * 100).toFixed(0) : 0;
            let color, icon;
            
            // Assign colors and icons based on progress
            if (progress >= 90) {
                color = '#33d685'; // Green for near completion
                icon = 'ri-check-double-line';
            } else if (progress >= 60) {
                color = '#506fd9'; // Blue for good progress
                icon = 'ri-arrow-up-circle-line';
            } else if (progress >= 30) {
                color = '#0dcaf0'; // Light blue for started
                icon = 'ri-arrow-right-circle-line';
            } else {
                color = '#6e7985'; // Gray for just started
                icon = 'ri-flag-2-line';
            }

            return {
                ...goal,
                name: goal.name,
                saved: goal.currentAmount || 0,
                target: goal.targetAmount || 0,
                deadline: goal.targetDate,
                progress: parseInt(progress),
                color,
                icon
            };
        });
    };

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <Alert 
                    variant="primary" 
                    className="mb-4 border-start border-primary border-4 shadow-sm"
                >
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="pe-3">
                            <h5 className="alert-heading mb-1">
                                <i className="ri-rocket-line me-2"></i>
                                Welcome 🚀
                            </h5>
                            <p className="mb-1">
                                You're among the first to experience our revolutionary financial management platform. We're constantly improving and your feedback is invaluable.
                            </p>
                            <p className="mb-0 mt-2">
                                <strong>Found a bug or have a suggestion?</strong>{' '}
                                <a href="/dashboard/contact" className="alert-link">Contact our dedicated support team</a>{' '}
                                {/* or email us at <span className="fw-semibold">support@dashbyte.com</span> */}
                            </p>
                        </div>
                        <Button 
                            variant="link" 
                            className="p-0 ms-2 text-primary" 
                            onClick={() => document.querySelector('.alert').remove()}
                            style={{ marginTop: '-60px' }}
                        >
                            <i className="ri-close-line fs-4"></i>
                        </Button>
                    </div>
                </Alert>

                <Row className="g-3 mb-5">
                    <Col xl="3" md="6">
                        <Card className="card-one">
                            <Card.Body>
                                <h3 className="card-value mb-1">${incomeStats.total.toFixed(2)}</h3>
                                <label className="card-title fw-medium text-dark mb-1">Total Income</label>
                                <ProgressBar now={Math.min((incomeStats.total / (incomeStats.total + expenseStats.total)) * 100, 100)} className="ht-5 mb-2" />
                                <span className="d-block fs-sm text-success">
                                    <i className="ri-arrow-up-line"></i> {((incomeStats.recurring / incomeStats.total) * 100).toFixed(1)}% recurring income
                                </span>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col xl="3" md="6">
                        <Card className="card-one">
                            <Card.Body>
                                <h3 className="card-value mb-1">${expenseStats.total.toFixed(2)}</h3>
                                <label className="card-title fw-medium text-dark mb-1">Total Expenses</label>
                                <ProgressBar now={Math.min((expenseStats.fixed / expenseStats.total) * 100, 100)} variant="danger" className="ht-5 mb-2" />
                                <span className="d-block fs-sm text-info">
                                    <i className="ri-information-line"></i> {((expenseStats.fixed / expenseStats.total) * 100).toFixed(1)}% fixed expenses
                                </span>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col xl="3" md="6">
                        <Card className="card-one">
                            <Card.Body>
                                <h3 className="card-value mb-1">${(incomeStats.total - expenseStats.total).toFixed(2)}</h3>
                                <label className="card-title fw-medium text-dark mb-1">Net Profit</label>
                                <ProgressBar now={Math.min(((incomeStats.total - expenseStats.total) / incomeStats.total) * 100, 100)} variant="success" className="ht-5 mb-2" />
                                <span className="d-block fs-sm text-success">
                                    <i className="ri-arrow-up-line"></i> {((incomeStats.total - expenseStats.total) / incomeStats.total * 100).toFixed(1)}% profit margin
                                </span>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col xl="3" md="6">
                        <Card className="card-one">
                            <Card.Body>
                                <h3 className="card-value mb-1">${billStats.total.toFixed(2)}</h3>
                                <label className="card-title fw-medium text-dark mb-1">Total Bills</label>
                                <ProgressBar now={Math.min((billStats.paid / billStats.total) * 100, 100)} variant="primary" className="ht-5 mb-2" />
                                <span className="d-block fs-sm text-primary">
                                    <i className="ri-check-line"></i> {((billStats.paid / billStats.total) * 100).toFixed(1)}% bills paid
                                </span>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Row className="g-3">

                    <Col md="12" lg="6">
                        <Card className="card-one">
                            <Card.Body className="overflow-hidden px-0 pb-3">
                                <div className="finance-info p-3 p-xl-4">
                                    <label className="fs-sm fw-medium mb-2 mb-xl-1">Financial Overview</label>
                                    <h1 className="finance-value">
                                        <span>$</span>{(incomeStats.total - expenseStats.total).toFixed(2)} <span>USD</span>
                                    </h1>

                                    <h4 className="finance-subvalue mb-3 mb-md-2">
                                        <i className={`ri-arrow-${incomeStats.total > expenseStats.total ? 'up' : 'down'}-line text-${incomeStats.total > expenseStats.total ? 'success' : 'danger'}`}></i>
                                        <span className={`text-${incomeStats.total > expenseStats.total ? 'success' : 'danger'}`}>
                                            {((Math.abs(incomeStats.total - expenseStats.total) / incomeStats.total) * 100).toFixed(1)}%
                                        </span>
                                        <small>Net Savings</small>
                                    </h4>

                                    <p className="w-50 fs-sm mb-2 mb-xl-4 d-none d-sm-block">
                                        Monthly overview of your income, expenses, and savings rate
                                    </p>

                                    <Row className="row-cols-auto g-3 g-xl-4 pt-2">
                                        <Col>
                                            <h6 className="card-value fs-15 mb-1">${(incomeStats.recurring || 0).toFixed(2)} USD</h6>
                                            <span className="fs-sm fw-medium text-secondary d-block mb-1">Recurring Income</span>
                                            <span className="fs-xs d-flex align-items-center ff-numerals text-success">
                                                {((incomeStats.recurring / incomeStats.total) * 100).toFixed(1)}% <i className="ri-arrow-up-line fs-15 lh-3"></i>
                                            </span>
                                        </Col>
                                        <Col>
                                            <h6 className="card-value fs-15 mb-1">${(expenseStats.fixed || 0).toFixed(2)} USD</h6>
                                            <span className="fs-sm fw-medium text-secondary d-block mb-1">Fixed Expenses</span>
                                            <span className="fs-xs d-flex align-items-center ff-numerals text-primary">
                                                {((expenseStats.fixed / expenseStats.total) * 100).toFixed(1)}% <i className="ri-bar-chart-line fs-15 lh-3"></i>
                                            </span>
                                        </Col>
                                        <Col>
                                            <h6 className="card-value fs-15 mb-1">${(billStats.paid || 0).toFixed(2)} USD</h6>
                                            <span className="fs-sm fw-medium text-secondary d-block mb-1">Bills Paid</span>
                                            <span className="fs-xs d-flex align-items-center ff-numerals text-info">
                                                {((billStats.paid / billStats.total) * 100).toFixed(1)}% <i className="ri-check-line fs-15 lh-3"></i>
                                            </span>
                                        </Col>
                                        <Col>
                                            <h6 className="card-value fs-15 mb-1">${(expenseStats.variable || 0).toFixed(2)} USD</h6>
                                            <span className="fs-sm fw-medium text-secondary d-block mb-1">Variable Expenses</span>
                                            <span className="fs-xs d-flex align-items-center ff-numerals text-warning">
                                                {((expenseStats.variable / expenseStats.total) * 100).toFixed(1)}% <i className="ri-funds-line fs-15 lh-3"></i>
                                            </span>
                                        </Col>
                                    </Row>
                                </div>

                                <Nav as="nav" className="nav-finance-one p-3 p-xl-4">
                                    <Link className="active">Monthly Overview</Link>
                                    <Link>Income Analysis</Link>
                                    <Link>Expense Trends</Link>
                                </Nav>

                                <ReactApexChart series={seriesOne} options={optionOne} type="area" height={430} className="apex-chart-two" />
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md="12" lg="6">
                        <Card className="card-one">
                            <Card.Header>
                                <Card.Title as="h6">Expense Vs Income</Card.Title>
                                <Nav as="nav" className="nav-icon nav-icon-sm ms-auto">
                                    <Nav.Link href=""><i className="ri-refresh-line"></i></Nav.Link>
                                    <Nav.Link href=""><i className="ri-more-2-fill"></i></Nav.Link>
                                </Nav>
                            </Card.Header>
                            <Card.Body>
                                <ReactApexChart series={series6} options={option6} type="bar" height={180} className="apex-chart-one mb-3" />
                                <p className="mb-3 fs-xs">Monthly comparison of income and expenses.</p>
                                <Row className="row-cols-auto g-3 g-xl-4 pt-2">
                                    <Col>
                                        <Card className="p-3 d-flex flex-row">
                                            <div className="card-icon bg-primary"><i className="ri-bar-chart-grouped-line"></i></div>
                                            <div className="ms-3">
                                                <h4 className="card-value mb-1">${expenseStats.total.toFixed(2)}</h4>
                                                <label className="card-title fw-medium text-dark mb-1">Total Expenses</label>
                                            </div>
                                        </Card>
                                    </Col>
                                    <Col>
                                        <Card className="p-3 d-flex flex-row">
                                            <div className="card-icon bg-ui-02"><i className="ri-bar-chart-grouped-line"></i></div>
                                            <div className="ms-3">
                                                <h4 className="card-value mb-1">${incomeStats.total.toFixed(2)}</h4>
                                                <label className="card-title fw-medium text-dark mb-1">Total Income</label>
                                            </div>
                                        </Card>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col xl="8">
                        <Card className="card-one">
                            <Card.Header>
                                <Card.Title as="h6">Expense Analytics</Card.Title>
                                <Nav className="nav-icon nav-icon-sm ms-auto">
                                    <Nav.Link href=""><i className="ri-refresh-line"></i></Nav.Link>
                                    <Nav.Link href=""><i className="ri-more-2-fill"></i></Nav.Link>
                                </Nav>
                            </Card.Header>
                            <Card.Body className="p-4">
                                <Row className="g-4">
                                    <Col md="12">
                                        <ReactApexChart series={seriesEight} options={optionEight} height={210} className="apex-chart-three" />
                                    </Col>
                                </Row>

                                <Row className="g-3 mt-2">
                                    <Col sm={6}>
                                        <Card className="card-one">
                                            <Card.Body className="p-3">
                                                <div className="d-flex align-items-center">
                                                    <div className="card-icon bg-primary">
                                                        <i className="ri-money-dollar-box-line"></i>
                                                    </div>
                                                    <div className="ms-3">
                                                        <h6 className="card-title mb-1">Fixed Expenses</h6>
                                                        <h3 className="card-value mb-0">${expenseStats.fixed?.toFixed(2) || '0.00'}</h3>
                                                        <span className="fs-xs text-secondary">
                                                            {((expenseStats.fixed / expenseStats.total) * 100).toFixed(1)}% of total expenses
                                                        </span>
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                    <Col sm={6}>
                                        <Card className="card-one">
                                            <Card.Body className="p-3">
                                                <div className="d-flex align-items-center">
                                                    <div className="card-icon bg-ui-02">
                                                        <i className="ri-funds-box-line"></i>
                                                    </div>
                                                    <div className="ms-3">
                                                        <h6 className="card-title mb-1">Variable Expenses</h6>
                                                        <h3 className="card-value mb-0">${expenseStats.variable?.toFixed(2) || '0.00'}</h3>
                                                        <span className="fs-xs text-secondary">
                                                            {((expenseStats.variable / expenseStats.total) * 100).toFixed(1)}% of total expenses
                                                        </span>
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>


                    <Col md="6" xl="4">
                        <Card className="card-one">
                            <Card.Header>
                                <Card.Title as="h6">Expenses By Category</Card.Title>
                                <Nav className="nav-icon nav-icon-sm ms-auto">
                                    <Nav.Link href=""><i className="ri-refresh-line"></i></Nav.Link>
                                    <Nav.Link href=""><i className="ri-more-2-fill"></i></Nav.Link>
                                </Nav>
                            </Card.Header>
                            <Card.Body>
                                <div className="chart-donut-one mb-3">
                                    <Doughnut data={dataPie} options={optionPie} />
                                </div>

                                <Table className="table-five">
                                    <tbody>
                                        {topCategories.data.length > 0 ? (
                                            topCategories.data.map((item, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <div>
                                                            <span className={`badge-dot bg-${index === 0 ? 'primary' : index === 1 ? 'ui-02' : 'teal'}`}></span>
                                                            <span className="fw-medium">{item.category}</span>
                                                        </div>
                                                    </td>
                                                    <td>${parseFloat(item.amount).toFixed(2)}</td>
                                                    <td>
                                                        <div className="justify-content-end text-success">
                                                            {item.percentage}% <i className="ri-arrow-up-line"></i>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="3" className="text-center">
                                                    No expense data available
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    </Col>



                    <Col md="6" xl="4">
                        <Card className="card-one">
                            <Card.Header>
                                <Card.Title as="h6">Billed Opportunities</Card.Title>
                                <Nav className="nav-icon nav-icon-sm ms-auto">
                                    <Nav.Link href=""><i className="ri-refresh-line"></i></Nav.Link>
                                    <Nav.Link href=""><i className="ri-more-2-fill"></i></Nav.Link>
                                </Nav>
                            </Card.Header>
                            <Card.Body className="position-relative">
                                {billStats.total > 0 ? (
                                    <>
                                        <ReactApexChart
                                            series={billData.series}
                                            options={billData.options}
                                            type="donut"
                                            height={300}
                                            className="apex-donut-two"
                                        />
                                        <div className="finance-donut-value mt-1">
                                            <h3>${billStats.total.toFixed(0)}</h3>
                                            <p>{((billStats.paid / billStats.total) * 100).toFixed(1)}% Paid</p>
                                        </div>
                                    </>
                                ) : (
                                    <div className="d-flex align-items-center justify-content-center h-100">
                                        <div className="text-center">
                                            <i className="ri-bill-line fs-2 text-muted mb-2"></i>
                                            <p className="mb-0">No bill data available</p>
                                        </div>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md="12" lg="8">
                        <Card className="card-one">
                            <Card.Header>
                                <Card.Title as="h6">Monthly Expenses Breakdown ({moment().format('MMMM YYYY')})</Card.Title>
                                <Nav className="nav-icon nav-icon-sm ms-auto">
                                    <Nav.Link href="/expense"><i className="ri-add-circle-line"></i></Nav.Link>
                                </Nav>
                            </Card.Header>
                            <Card.Body className="p-4">
                                {getMonthlyExpensesBreakdown().categories.length === 0 ? (
                                    <div className="text-center py-4">
                                        <i className="ri-pie-chart-line fs-2 text-muted mb-2"></i>
                                        <p className="mb-1">No expense data for {moment().format('MMMM YYYY')}</p>
                                        <Link to="/expense" className="btn btn-sm btn-primary">
                                            <i className="ri-add-line me-1"></i> Add Expense
                                        </Link>
                                    </div>
                                ) : (
                                    <>
                                        <div className="progress mb-4" style={{ height: '8px', backgroundColor: '#f0f1f7', borderRadius: '4px', overflow: 'hidden' }}>
                                            {getMonthlyExpensesBreakdown().categories.map((category, index) => (
                                                <div
                                                    key={index}
                                                    className="progress-bar"
                                                    role="progressbar"
                                                    style={{
                                                        width: `${category.percentage}%`,
                                                        background: `linear-gradient(45deg, ${category.color}88, ${category.color})`,
                                                        borderRight: index < getMonthlyExpensesBreakdown().categories.length - 1 ? '2px solid #fff' : 'none',
                                                        transition: 'width 0.5s ease'
                                                    }}
                                                    aria-valuenow={category.percentage}
                                                    aria-valuemin="0"
                                                    aria-valuemax="100"
                                                />
                                            ))}
                                        </div>
                                        {getMonthlyExpensesBreakdown().categories.map((category, index) => (
                                            <div key={index} className="d-flex align-items-center justify-content-between mb-3">
                                                <div className="d-flex align-items-center">
                                                    <span className="badge-dot me-2" style={{ backgroundColor: category.color }}></span>
                                                    <span className="fw-medium">{category.name}</span>
                                                </div>
                                                <div className="d-flex align-items-center gap-3">
                                                    <span className="text-dark fw-medium">${category.amount.toFixed(2)}</span>
                                                    <span className="text-secondary" style={{ minWidth: '46px' }}>{category.percentage}%</span>
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                </Row>

                <Row className="g-3 mt-4">

                <Col md="12" lg="6">
                        <Card className="card-one">
                            <Card.Header>
                                <Card.Title as="h6">Monthly Budgets ({moment().format('MMMM YYYY')})</Card.Title>
                                <Nav className="nav-icon nav-icon-sm ms-auto">
                                    <Nav.Link href="/budget"><i className="ri-add-circle-line"></i></Nav.Link>
                                </Nav>
                            </Card.Header>
                            <Card.Body className="p-4">
                                {getMonthlyBudgets().budgetItems.length === 0 ? (
                                    <div className="text-center py-4">
                                        <i className="ri-wallet-line fs-2 text-muted mb-2"></i>
                                        <p className="mb-1">No active budgets for {moment().format('MMMM YYYY')}</p>
                                        <Link to="/budget" className="btn btn-sm btn-primary">
                                            <i className="ri-add-line me-1"></i> Set Budget
                                        </Link>
                                    </div>
                                ) : (
                                    getMonthlyBudgets().budgetItems.map((budget, index) => (
                                        <div key={index} className="mb-4">
                                            <div className="d-flex align-items-center justify-content-between mb-1">
                                                <div>
                                                    <span className="fw-medium">{budget.name}</span>
                                                    <small className="d-block text-secondary">
                                                        {moment(budget.startDate).format('MMM D')} - {moment(budget.endDate).format('MMM D, YYYY')}
                                                    </small>
                                                </div>
                                                <div className="text-end">
                                                    <span className="d-block fw-medium">${budget.spent.toFixed(2)}</span>
                                                    <small className="text-secondary">of ${budget.limit.toFixed(2)}</small>
                                                </div>
                                            </div>
                                            <div className="progress ht-10">
                                                <div 
                                                    className="progress-bar" 
                                                    role="progressbar" 
                                                    style={{ 
                                                        width: `${budget.progress}%`,
                                                        backgroundColor: budget.color,
                                                        transition: 'width 0.5s ease'
                                                    }} 
                                                    aria-valuenow={budget.progress} 
                                                    aria-valuemin="0" 
                                                    aria-valuemax="100"
                                                >
                                                    {budget.progress >= 25 && `${budget.progress.toFixed(0)}%`}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md="12" lg="6">
                        <Card className="card-one">
                            <Card.Header>
                                <Card.Title as="h6">Saving Goals</Card.Title>
                                <Nav className="nav-icon nav-icon-sm ms-auto">
                                    <Nav.Link href="/goal"><i className="ri-add-circle-line"></i></Nav.Link>
                                </Nav>
                            </Card.Header>
                            <Card.Body className="p-4">
                                {getSavingGoals().length === 0 ? (
                                    <div className="text-center py-4">
                                        <i className="ri-coins-line fs-2 text-muted mb-2"></i>
                                        <p className="mb-1">No saving goals yet</p>
                                        <Link to="/goal" className="btn btn-sm btn-primary">
                                            <i className="ri-add-line me-1"></i> Add Goal
                                        </Link>
                                    </div>
                                ) : (
                                    <Row className="g-3">
                                        {getSavingGoals().map((goal, index) => (
                                            <Col key={index} xs="6" className="text-center">
                                                <Card className="card-one p-3">
                                                    <div className="position-relative d-inline-block mb-2">
                                                        <ReactApexChart
                                                            options={{
                                                                chart: { type: 'radialBar' },
                                                                plotOptions: {
                                                                    radialBar: {
                                                                        hollow: { size: '65%' },
                                                                        track: { 
                                                                            background: '#f0f1f7',
                                                                            strokeWidth: '100%',
                                                                            margin: 0
                                                                        },
                                                                        dataLabels: {
                                                                            name: { show: false },
                                                                            value: {
                                                                                offsetY: 5,
                                                                                fontSize: '14px',
                                                                                fontWeight: '600',
                                                                                formatter: function(val) { return val + '%' }
                                                                            }
                                                                        }
                                                                    }
                                                                },
                                                                colors: [goal.color],
                                                                stroke: { lineCap: 'round' },
                                                                states: {
                                                                    hover: { filter: { type: 'none' } },
                                                                    active: { filter: { type: 'none' } }
                                                                }
                                                            }}
                                                            series={[goal.progress]}
                                                            type="radialBar"
                                                            height={140}
                                                            width={140}
                                                        />
                                                        <div className="goal-icon" style={{
                                                            position: 'absolute',
                                                            top: '50%',
                                                            left: '50%',
                                                            transform: 'translate(-50%, -90%)'
                                                        }}>
                                                            <i className={`${goal.icon} fs-18`} style={{ color: goal.color }}></i>
                                                        </div>
                                                    </div>
                                                    <h6 className="mb-1">{goal.name}</h6>
                                                    <div className="d-flex align-items-center justify-content-center gap-1">
                                                        <small className="text-dark fw-medium">${goal.saved?.toFixed(2) || '0.00'}</small>
                                                        <small className="text-secondary">/ ${goal.target?.toFixed(2)}</small>
                                                    </div>
                                                    <small className="d-block text-secondary">
                                                        {goal.deadline ? moment(goal.deadline).format('MMM DD, YYYY') : 'No deadline'}
                                                    </small>
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Footer />
            </div>
        </React.Fragment>
    );
}
